import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AuthGuard } from './components/AuthGuard';
import { AuthCallback } from './components/AuthCallback';
import { Unauthorized } from './pages/Unauthorized';
import { Login } from './pages/Login';
import { AdministratorDashboard } from './pages/AdministratorDashboard';
import { UserProfile } from './pages/UserProfile';
import { ParentDashboard } from './pages/ParentDashboard';
import { AgeGroupsManagement } from './pages/AgeGroupsManagement';
import { EvaluationSessions } from './pages/EvaluationSessions';
import Evaluation from './pages/Evaluation';
import CoachPortal from './pages/CoachPortal';
import ManageMembers from './pages/ManageMembers';
import CheckIn from './pages/CheckIn';
import PlayerManagement from './pages/PlayerManagement';
import Reports from './pages/Reports';
import TeamsManagement from './pages/TeamsManagement';
import TeamRoster from './pages/TeamRoster';
import { useAuth } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import AgeGroupTryoutInfo from './pages/AgeGroupTryoutInfo';
import ShirtSizeSelector from './pages/ShirtSizeSelector';
import PlayerDetails from './pages/PlayerDetails';
import EditPlayer from './pages/EditPlayer';

// Component to redirect users based on their role
function RoleBasedRedirect() {
  const { profile } = useAuth();
  
  if (!profile) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>;
  }
  
  switch (profile.role) {
    case 'administrator':
      return <Navigate to="/administration" replace />;
    case 'coach':
      return <Navigate to="/coach" replace />;
    case 'parent':
      return <Navigate to="/parent" replace />;
    case 'player':
      // If you want to keep the player dashboard, you can create a dedicated player page
      return <Navigate to="/player" replace />;
    default:
      return <Navigate to="/profile" replace />;
  }
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Publicly accessible routes */}
          <Route path="/today" element={<CheckIn />} />
          <Route path="/checkin" element={<Navigate to="/" replace />} />
          <Route path="/tryout-info" element={<AgeGroupTryoutInfo />} />
          <Route path="/shirt-size/:playerId" element={<ShirtSizeSelector />} />

          {/* Profile route available to any logged-in user */}
          <Route
            path="/profile"
            element={
              <AuthGuard>
                <UserProfile />
              </AuthGuard>
            }
          />

          {/* Role-specific routes */}
          <Route
            path="/administrator"
            element={
              <AuthGuard>
                <Navigate to="/administration" replace />
              </AuthGuard>
            }
          />
          <Route
            path="/administration"
            element={
              <AuthGuard allowedRoles={['administrator']}>
                <AdministratorDashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/coach"
            element={
              <AuthGuard allowedRoles={['coach', 'administrator']}>
                <CoachPortal />
              </AuthGuard>
            }
          />
          <Route
            path="/evaluation/:evaluationId"
            element={
              <AuthGuard allowedRoles={['coach', 'administrator']}>
                <Evaluation />
              </AuthGuard>
            }
          />
          <Route
            path="/parent"
            element={
              <AuthGuard allowedRoles={['parent', 'coach', 'administrator']}>
                <ParentDashboard />
              </AuthGuard>
            }
          />

          {/* Age Groups Management route */}
          <Route
            path="/age-groups"
            element={
              <AuthGuard allowedRoles={['administrator']}>
                <AgeGroupsManagement />
              </AuthGuard>
            }
          />

          {/* Evaluation Sessions route */}
          <Route
            path="/evaluation-sessions"
            element={
              <AuthGuard allowedRoles={['administrator']}>
                <EvaluationSessions />
              </AuthGuard>
            }
          />

          {/* Manage Members route */}
          <Route
            path="/members"
            element={
              <AuthGuard allowedRoles={['administrator']}>
                <ManageMembers />
              </AuthGuard>
            }
          />

          {/* Manage Players route */}
          <Route
            path="/players"
            element={
              <AuthGuard allowedRoles={['administrator', 'coach']}>
                <PlayerManagement />
              </AuthGuard>
            }
          />

          {/* Reports route */}
          <Route
            path="/reports"
            element={
              <AuthGuard allowedRoles={['administrator']}>
                <Reports />
              </AuthGuard>
            }
          />

          {/* Teams Management route */}
          <Route
            path="/teams"
            element={
              <AuthGuard allowedRoles={['administrator']}>
                <TeamsManagement />
              </AuthGuard>
            }
          />

          {/* Team Roster route */}
          <Route
            path="/team-roster/:teamId"
            element={
              <AuthGuard allowedRoles={['coach', 'administrator']}>
                <TeamRoster />
              </AuthGuard>
            }
          />

          {/* Player Details route */}
          <Route
            path="/player/:playerId/:teamId"
            element={
              <AuthGuard allowedRoles={['coach', 'administrator']}>
                <PlayerDetails />
              </AuthGuard>
            }
          />

          {/* Edit Player route */}
          <Route
            path="/player/:playerId/edit"
            element={
              <AuthGuard allowedRoles={['coach', 'administrator']}>
                <EditPlayer />
              </AuthGuard>
            }
          />

          {/* Default route - Redirect based on user role */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <RoleBasedRedirect />
              </AuthGuard>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;